/**
 * The Whisper scene manifest — the parallax "set" for the séance game.
 *
 * v1 ships with `layers: []` for EVERY scene, so SceneStack renders each scene's
 * `fallbackGradient` — an arcana violet-and-brass wash — until bespoke art lands.
 * The game is fully playable and on-theme (arcana palette + `arcanaWisps`) before
 * a single pixel of scene art exists, exactly like the Blood Moon manifest.
 *
 * The skin is the SWAP (THE_WHISPER_SPEC.md §2): the arcana ramp (--arcana-*),
 * violet wisps orbiting (the candle glow + clue runes reuse `arcanaWisps`), and
 * the Seer's tent mood. No blood accent anywhere.
 *
 * ── ART CONTRACT (for the asset agent) ────────────────────────────────────────
 * Scene art lands at stable webp paths under this directory
 * (packages/client/src/assets/scenes/whisper/). When a file exists, import it and
 * push it into the matching scene's `layers` array (depth / slot / drift like
 * bloodmoon.ts). Until then the arcana gradient carries the whole set.
 *
 *   tent-sky.webp       sky   depth .00  (violet-black séance drape)
 *   tent-glow.webp      sky   depth .02  blend:screen  pulse (candle-lit swell)
 *   tent-drape.webp     mid   depth .10  drift ±30px / 70s
 *   tent-fore.webp      fore  depth .18  drift ±16px / 100s
 * ──────────────────────────────────────────────────────────────────────────────
 */

import type { SceneManifest } from './types.js';

// Fallback gradients — arcana ink→violet washes. No layers yet, so these carry
// the entire look. Base is the shared near-black (#080808); the low band bleeds
// toward the arcana ramp so the Seer's tent reads violet, never crimson.
const GRAD_TENT = 'linear-gradient(180deg, #080808 0%, #160b26 100%)'; // --arcana-0
const GRAD_TENT_DEEP = 'linear-gradient(180deg, #080808 0%, #1e0f34 100%)';
const GRAD_HUNT = 'linear-gradient(180deg, #080808 0%, #2c1650 100%)'; // --arcana-1
const GRAD_SCORE = 'linear-gradient(180deg, #080808 0%, #1a1030 100%)';

const ARCANA_ACCENT = '#8b5cf6'; // --arcana-3

export const whisperScenes: SceneManifest = {
  gameId: 'whisper',
  scenes: {
    // The lobby breathes calm — a lit tent waiting to fill. Sparse wisps.
    LOBBY: {
      layers: [],
      particles: [{ preset: 'arcanaWisps', intensity: 0.4, slot: 'fog' }],
      accent: ARCANA_ACCENT,
      fallbackGradient: GRAD_TENT,
    },
    // The candle-lit interrogation. The wisps thicken — the Seer is present.
    TENT: {
      layers: [],
      particles: [{ preset: 'arcanaWisps', intensity: 0.7, slot: 'fog' }],
      accent: ARCANA_ACCENT,
      fallbackGradient: GRAD_TENT_DEEP,
    },
    // The hunt — the table turns on itself. Deepest violet, ash drifting.
    HUNT: {
      layers: [],
      particles: [
        { preset: 'arcanaWisps', intensity: 0.5, slot: 'fog' },
        { preset: 'ash', intensity: 0.3, slot: 'fore' },
      ],
      accent: ARCANA_ACCENT,
      fallbackGradient: GRAD_HUNT,
    },
    // The scoreboard — arcana shimmer, a touch of gold for the winner glow.
    SCORE: {
      layers: [],
      particles: [{ preset: 'goldDust', intensity: 0.5, slot: 'fog' }],
      accent: '#c9b6ff', // --arcana-4
      fallbackGradient: GRAD_SCORE,
    },
  },
  // Phase → scene. RESOLVE_GUESS is transient (never broadcast) but must map for
  // the total Record<Phase, string>; it shares TENT. Blood Moon phases share TENT
  // as inert placeholders (a Whisper room never enters them).
  phaseToScene: {
    LOBBY: 'LOBBY',
    WHISPER_INTRO: 'TENT',
    QUESTION: 'TENT',
    RESOLVE_GUESS: 'TENT',
    HUNT: 'HUNT',
    RESOLVE_HUNT: 'HUNT',
    SCORE: 'SCORE',
    END: 'SCORE',
    // Blood Moon phases — never entered by a Whisper room; mapped to TENT so the
    // Record stays total.
    DEAL: 'TENT',
    NIGHT_INTRO: 'TENT',
    NIGHT_WOLVES: 'TENT',
    NIGHT_INTERLUDE: 'TENT',
    NIGHT_SEER: 'TENT',
    RESOLVE_NIGHT: 'TENT',
    DAWN: 'TENT',
    DISCUSS: 'TENT',
    VOTE: 'HUNT',
    RESOLVE_VOTE: 'HUNT',
    // One Night phases — never entered by a Whisper room; mapped to TENT/HUNT so
    // the Record stays total.
    ONENIGHT_NIGHTFALL: 'TENT',
    ONENIGHT_NIGHT_ACTIONS: 'TENT',
    ONENIGHT_DAWN: 'TENT',
    ONENIGHT_DISCUSS: 'TENT',
    ONENIGHT_VOTE: 'HUNT',
    ONENIGHT_RESOLVE: 'HUNT',
  },
  // Dim the scene stack against the ink base. Same treatment as Blood Moon: the
  // lobby reads calmest, gameplay phases hold a touch darker so the UI sits above.
  phaseDim: {
    LOBBY: 0.5,
    WHISPER_INTRO: 0.6,
    QUESTION: 0.6,
    RESOLVE_GUESS: 0.6,
    HUNT: 0.65,
    RESOLVE_HUNT: 0.65,
    SCORE: 0.55,
    END: 0.55,
  },
};
